/**
 * Logger utility for debugging the party game application
 * Logs are stored in memory and can be downloaded as a file
 */

export interface LogEntry {
  timestamp: string;
  level: 'info' | 'warn' | 'error' | 'debug';
  category: string;
  message: string;
  data?: unknown;
}

class Logger {
  private logs: LogEntry[] = [];
  private maxLogs = 1000;

  private formatTimestamp(): string {
    return new Date().toISOString();
  }

  private addLog(entry: LogEntry) {
    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Also log to console with colors
    const colors = {
      info: 'color: #3b82f6',
      warn: 'color: #f59e0b',
      error: 'color: #ef4444',
      debug: 'color: #8b5cf6',
    };

    const prefix = `[${entry.category}]`;
    
    if (entry.level === 'error') {
      console.error(`%c${prefix}`, colors[entry.level], entry.message, entry.data || '');
    } else if (entry.level === 'warn') {
      console.warn(`%c${prefix}`, colors[entry.level], entry.message, entry.data || '');
    } else {
      console.log(`%c${prefix}`, colors[entry.level], entry.message, entry.data || '');
    }
  }

  info(category: string, message: string, data?: unknown) {
    this.addLog({
      timestamp: this.formatTimestamp(),
      level: 'info',
      category,
      message,
      data,
    });
  }

  warn(category: string, message: string, data?: unknown) {
    this.addLog({
      timestamp: this.formatTimestamp(),
      level: 'warn',
      category,
      message,
      data,
    });
  }

  error(category: string, message: string, data?: unknown) {
    this.addLog({
      timestamp: this.formatTimestamp(),
      level: 'error',
      category,
      message,
      data,
    });
  }

  debug(category: string, message: string, data?: unknown) {
    this.addLog({
      timestamp: this.formatTimestamp(),
      level: 'debug',
      category,
      message,
      data,
    });
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  getLogsAsText(): string {
    return this.logs
      .map((log) => {
        const dataStr = log.data ? ` | Data: ${JSON.stringify(log.data)}` : '';
        return `[${log.timestamp}] [${log.level.toUpperCase()}] [${log.category}] ${log.message}${dataStr}`;
      })
      .join('\n');
  }

  downloadLogs() {
    const text = this.getLogsAsText();
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `game-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  clear() {
    this.logs = [];
  }
}

export const logger = new Logger();

// Make logger available globally for debugging
if (typeof window !== 'undefined') {
  (window as unknown as { gameLogger: Logger }).gameLogger = logger;
}
